import { datadogRum } from '@datadog/browser-rum-slim';
import { FetcherError } from 'shared/utils/fetcher';
import { MONITORING_TAGS } from 'constants/monitoring';
import { UpscribeErrorObject } from '../types';
import UpscribeRequestError from './upscribeRequestError';
import { toSentenceCase } from '../../../../shared/utils/convertCase';
import { replaceStringByRules } from '../../../../shared/utils/replaceString';
import { errorMessagReplaceRules, statusMessages } from '../upscribeRequest.constants';

type ErrorDetails = {
  status?: number;
  url?: string;
  method?: string;
  stack?: string;
};
type LoggerFunction = (error: Error, context: unknown) => void;

const extractErrorDetails = (error: unknown): ErrorDetails => {
  let status: number | undefined;
  let url: string | undefined;
  let method: string | undefined;
  let stack: string | undefined;

  if (error instanceof FetcherError) {
    status = error.status;
    url = error.url;
    method = error.method;
    stack = error.stack;
  }

  return { status, url, method, stack };
};

const tags = {
  [MONITORING_TAGS.UPSCRIBE]: true
};

const logError = (
  {
    errorObject,
    error,
    requestStartTime,
    extra = null
  }: {
    errorObject: UpscribeRequestError;
    error: unknown;
    requestStartTime: number;
    extra?: unknown;
  },
  loggerFunction: LoggerFunction
): void => {
  const { status, url, method, stack } = extractErrorDetails(error);

  try {
    const loggingStatus = status ? `[${status}] ` : '';
    const errorForLogging = new Error(`${loggingStatus}Upscribe Error: ${errorObject.message}`);

    loggerFunction(errorForLogging, {
      level: 'debug',
      tags,
      contexts: {
        stack,
        request: {
          method,
          url
        },
        response: {
          duration: performance.now() - requestStartTime,
          status
        }
      },
      extra
    });
  } catch (e) {
    datadogRum.addError(e as Error, { tags });
  }
};

export const sendUpscribeExceptionToDatadog = (
  errorObject: UpscribeRequestError,
  error: unknown,
  requestStartTime: number
): void => {
  logError({ errorObject, error, requestStartTime }, datadogRum.addError);
};

const getMessageFromUpscribeErrorObject = (error: UpscribeErrorObject) => {
  let errorMessageString = '';
  const errors = error.errors || {};
  Object.entries(errors).forEach(([groupKey, groupValue]) => {
    if (Array.isArray(groupValue)) {
      groupValue.forEach((group) => {
        if (typeof group === 'string') {
          errorMessageString += `${group}`;
        } else {
          errorMessageString += `${group?.message}.`;
        }
      });
    } else {
      Object.entries(groupValue).forEach(([groupKeySecond, groupValueSecond]) => {
        if (Array.isArray(groupValueSecond)) {
          groupValueSecond.forEach((groupItem) => {
            errorMessageString += `${toSentenceCase(groupKey)}: ${toSentenceCase(groupKeySecond)} ${
              groupItem.message
            }.`;
          });
        } else {
          Object.values(groupValueSecond).forEach((groupValueThird) => {
            if (!Array.isArray(groupValueThird)) {
              errorMessageString += `${toSentenceCase(groupKey)}: ${toSentenceCase(
                groupKeySecond
              )} ${groupValueThird.message}.`;
            } else if (Array.isArray(groupValueThird)) {
              groupValueThird.forEach((groupItem) => {
                errorMessageString += `${toSentenceCase(groupKey)}:  ${groupItem.message}.`;
              });
            }
          });
        }
      });
    }
  });

  return errorMessageString;
};

export function upscribeRequestOnError(error: unknown, requestStartTime: number) {
  let message = '';
  let status: number | undefined;
  let headers: Record<string, string> | undefined;
  let data: any = { error };

  if (error instanceof FetcherError) {
    status = error.status;
    headers = error.headers;
    data = error.data;
    message = error.message;
  }
  const errorObject = new UpscribeRequestError(
    'Unknown error with no context',
    status,
    headers,
    data
  );

  if (!status && !data && !headers) {
    sendUpscribeExceptionToDatadog(errorObject, error, requestStartTime);
    return Promise.reject(errorObject);
  }

  // Parse error message from response
  if (typeof data?.error === 'string') {
    errorObject.message = data.error;
  } else if (typeof data?.error?.message === 'string') {
    errorObject.message = replaceStringByRules(data.error.message, errorMessagReplaceRules);
  } else if (typeof data?.error?.errors === 'object') {
    errorObject.message = getMessageFromUpscribeErrorObject(data.error);
  } else if (typeof data === 'string') {
    errorObject.message = data;
  } else if (status && statusMessages[status]) {
    errorObject.message = statusMessages[status];
  } else if (message) {
    // If nothing was in the response, use the error message
    errorObject.message = message;
  }

  // then log the error to Datadog and throw it
  sendUpscribeExceptionToDatadog(errorObject, error, requestStartTime);

  return Promise.reject(errorObject);
}
